// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@sc/Overrides/Components/RfqQuoteDetailsPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableRow, Typography } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface CmsProperties {
    showQuoteNumber?: boolean;
    showType?: boolean;
    showStatus?: boolean;
    showRequested?: boolean;
    showShipTo?: boolean;
    showExpires?: boolean;
    showSalesReq?: boolean;
    showUser?: boolean;
    showCustomer?: boolean;
}

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
    // XNG-Changes
    cmsProperties?: CmsProperties;
}

const mapStateToProps = (state: ApplicationState) => ({
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    language: state.context.session.language,
    xGoogleAnalytics: (state as xGoogleAnalyticsState).xGoogleAnalytics,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    dataCells?: DataTableCellProps;
    clickableCell?: LinkPresentationProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {
    quoteSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: {
        gap: 5,
    },
    quoteDetailsLinkGridItem: { width: 12 },
    typeGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    orderDateGridItem: { width: 6 },
    expirationDateGridItem: { width: 6 },
    salesRepGridItem: { width: 6 },
    userNameGridItem: { width: 6 },
    customerGridItem: { width: 6 },
    shipToAddressGridItem: { width: 6 },
};

const QuoteSummaryCard: FC<Props> = ({
    quote,
    session,
    quoteSettings,
    extendedStyles,
    cmsProperties,
    // XNG-Change
    xGoogleAnalytics,
}) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    // XNG-Changes
    const trackQuoteClick = (clickText: string) => {
        if (xGoogleAnalytics?.myacctQuotesEvent && xGoogleAnalytics?.myacctQuotesEventName) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: xGoogleAnalytics.myacctQuotesEventName,
                quote_name: quote.quoteNumber || "",
                click_text: clickText,
                affiliation: xGoogleAnalytics.affiliation || "",
                sales_rep: xGoogleAnalytics.salesRep || "",
                is_rep: xGoogleAnalytics.isRep || "",
                bill_to_num: xGoogleAnalytics.billToNum || "",
                ship_to_num: xGoogleAnalytics.shipToNum || "",
                cust_type: xGoogleAnalytics.customerType || "",
                login_type: xGoogleAnalytics.loginType || "",
                warehouse: xGoogleAnalytics.warehouse || "",
            };

            dataLayer.push({ ecommerce: null });
            dataLayer.push(googleAnalyticsData);
        }
    };

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
            },
        });
    }
    const customerDisplay = `${quote.customerNumber} - ${quote.customerName}`;
    const shipToDisplay = quote.shipTo && `${quote.shipTo?.customerSequence} - ${quote.shipTo?.customerName}`;

    return (
        <DataTableRow data-test-selector="recentQuotes">
            {cmsProperties?.showRequested && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <Typography>{orderDateDisplay}</Typography>
                </DataTableCell>
            )}
            {cmsProperties?.showQuoteNumber && (
                <DataTableCell {...styles.clickableCell}>
                    <RfqQuoteDetailsPageTypeLink
                        title={quote.quoteNumber}
                        quoteId={quote.id}
                        // XNG-Changes
                        onClick={() => trackQuoteClick(quote.quoteNumber || "")}
                    />
                </DataTableCell>
            )}
            {cmsProperties?.showType && quoteSettings.jobQuoteEnabled && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <Typography>{quote.typeDisplay}</Typography>
                </DataTableCell>
            )}
            {cmsProperties?.showStatus && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <Typography>{quote.statusDisplay}</Typography>
                </DataTableCell>
            )}

            {cmsProperties?.showExpires && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <Typography>{expirationDateDisplay || "- -"}</Typography>
                </DataTableCell>
            )}
            {session.isSalesPerson && (
                <>
                    {cmsProperties?.showSalesReq && (
                        <DataTableCell {...styles.dataCells}>
                            {/* XNG-Changes: make only first cell clicakble */}
                            <Typography>{quote.salespersonName}</Typography>
                        </DataTableCell>
                    )}
                    {cmsProperties?.showUser && (
                        <DataTableCell {...styles.dataCells} data-test-selector="user">
                            {/* XNG-Changes: make only first cell clicakble */}
                            <Typography>{quote.userName}</Typography>
                        </DataTableCell>
                    )}
                </>
            )}
            {cmsProperties?.showCustomer && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <Typography>{customerDisplay}</Typography>
                </DataTableCell>
            )}

            {!session.isSalesPerson && cmsProperties?.showShipTo && (
                <DataTableCell {...styles.dataCells}>
                    {/* XNG-Changes: make only first cell clicakble */}
                    <Typography>{shipToDisplay}</Typography>
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(QuoteSummaryCard);
